import { useWelcomeOffer, WelcomePrice } from '../offers/WelcomeOfferContext';
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CaptionProvider, useCaptions } from './context/CaptionContext';
import VideoPreview from './components/VideoPreview';
import Sidebar from './components/Sidebar';
import FileInputPanel from './components/FileInputPanel';
import CaptionEditor from './components/CaptionEditor';
import TextProseRenderer from './components/TextProseRenderer';
import SearchBar from './components/SearchBar';
import TipsPanel from './components/TipsPanel';
import TutorialsPanel from './components/TutorialsPanel';
import PremiereEditorCard from '../components/premiere/PremiereEditorCard';
import { useSearch } from './hooks/useSearch';
import { EditorSkeleton, EditorContentSkeleton } from '../components/Skeleton';
import { EditorTour, shouldShowEditorTour, SettingsTour, shouldShowSettingsTour } from '../components/DashboardTour';
import { useAuth } from '../auth/AuthContext';
import PhoneVerificationModal from '../components/PhoneVerificationModal';
import verificationService from '../api/verificationService';
import { userService } from '../api/userService';
import { subscriptionService } from '../api/subscriptionService';
import { editorService } from '../api/editorService';
import { detectCountry, getDisplayCurrency, getAllPlanPrices, getCachedCountry } from '../utils/pricing';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useI18n, rich } from '../i18n';

// Icons
const ChevronLeftIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rtl:-scale-x-100">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const UndoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
    <path d="M3 7v6h6" />
    <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13" />
  </svg>
);

const RedoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
    <path d="M21 7v6h-6" />
    <path d="M3 17a9 9 0 019-9 9 9 0 016 2.3l3 2.7" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// NLE brand logos (official icons from /public/icons)
const NleLogo = ({ src, alt, size = 28, className = '' }) => (
  <img
    src={src}
    alt={alt}
    width={size}
    height={size}
    className={`rounded-[6px] object-contain shrink-0 ${className}`}
    draggable={false}
  />
);

const PremiereProLogo = ({ size = 28 }) => (
  <NleLogo src="/icons/premiere-pro.png" alt="Premiere Pro" size={size} />
);

const DaVinciLogo = ({ size = 28 }) => (
  <NleLogo src="/icons/davinci-resolve.png" alt="DaVinci Resolve" size={size} />
);

const CapCutLogo = ({ size = 28 }) => (
  <NleLogo
    src="/icons/capcut.png"
    alt="CapCut"
    size={size}
    className="bg-white border border-slate-200 p-0.5"
  />
);

const FinalCutLogo = ({ size = 28 }) => (
  <NleLogo src="/icons/final-cut-pro.png" alt="Final Cut Pro" size={size} />
);

const NLE_EXPORT_GUIDES = {
  premiere: {
    id: 'premiere',
    name: 'Adobe Premiere Pro',
    accent: '#9999FF',
    Logo: PremiereProLogo,
    steps: [
      'Your .srt file has been downloaded. Open your project in Adobe Premiere Pro.',
      'Go to File → Import (or press Cmd+I / Ctrl+I) and select the downloaded .srt file.',
      'In the Project panel, drag the imported captions onto your sequence timeline.',
      'When prompted for caption format, choose Subtitle (best for styling). Use Source Timecode to keep sync.',
      'Captions appear on a C1 track above your video. Open Window → Essential Graphics (or Text) to style font, size, color, and position.',
      'Select all caption clips to apply the same style everywhere, then export with captions burned in or as a sidecar.',
    ],
  },
  davinci: {
    id: 'davinci',
    name: 'DaVinci Resolve',
    accent: '#FF6B35',
    Logo: DaVinciLogo,
    steps: [
      'Your .srt file has been downloaded. Open your project in DaVinci Resolve (Edit page).',
      'Go to File → Import → Subtitle… and select the downloaded .srt file.',
      'Alternatively: right-click the timeline header → Add Subtitle Track, then drag the .srt from the Media Pool onto that track.',
      'Subtitles appear on a dedicated subtitle track above your video, timed to the SRT cues.',
      'Select a subtitle and open the Inspector to adjust font, size, color, stroke, background, and position.',
      'When exporting, burn subtitles into the video or include them as a separate subtitle stream.',
    ],
  },
  capcut: {
    id: 'capcut',
    name: 'CapCut',
    accent: '#00C2B8',
    Logo: CapCutLogo,
    steps: [
      'Your .srt file has been downloaded. Open CapCut Desktop or CapCut Web.',
      'Open your video project, then go to Text / Captions.',
      'Choose Import Captions / Import Subtitles (or Captions → Add Captions) and select the .srt file. You can also drag it onto the timeline.',
      'CapCut creates editable text clips for each cue, synced to the timestamps.',
      'Select a caption and use the right panel to change font, size, color, position, and animations. Use “Apply to all” if available.',
      'Preview the timeline, then export your video with captions burned in.',
    ],
  },
  finalcut: {
    id: 'finalcut',
    name: 'Final Cut Pro',
    accent: '#5AC8FA',
    Logo: FinalCutLogo,
    steps: [
      'Your .srt file has been downloaded. Open your project in Final Cut Pro.',
      'Go to File → Import → Captions… and select the downloaded .srt file.',
      'Captions appear in a dedicated caption lane above the primary storyline.',
      'Double-click a caption (or use the caption editor) to fix text or tweak timing if needed.',
      'Style captions from the inspector (font, size, color, position) so they match your project.',
      'On export, include closed captions or burn subtitles into the video as required by your platform.',
    ],
  },
};

const UploadIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TranslateIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 19l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const LoadingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
    <path d="M21 12a9 9 0 11-6.219-8.56" />
  </svg>
);

// Simplified read-only prose view for mobile
function MobileProseView({ 
  captionData, 
  currentTime, 
  themeSettings, 
  getDisplayText, 
  displayMode, 
  getPhraseForWord,
  seekToTime,
  phraseDisplayMode,
  applyPhraseDisplayMode,
  hasPhrases,
  setThemeSettings
}) {
  const { t } = useI18n();
  const containerRef = useRef(null);
  const activeWordRef = useRef(null);
  const isArabizi = displayMode === 'arabizi';
  const showPunctuation = themeSettings.showPunctuation !== false;

  // Get all words from skeletons
  const wordsMap = useMemo(() => {
    return captionData.words.reduce((acc, word) => {
      acc[word.id] = word;
      return acc;
    }, {});
  }, [captionData.words]);

  const activeSkeletons = useMemo(() => {
    return captionData.skeleton.filter(skel => !skel.disabled);
  }, [captionData.skeleton]);

  const allWords = useMemo(() => {
    const words = [];
    activeSkeletons.forEach(skeleton => {
      skeleton.words.forEach(id => {
        const word = wordsMap[id];
        if (word && word.type !== 'silence') {
          words.push({ word, skeletonId: skeleton.id });
        }
      });
    });
    return words;
  }, [activeSkeletons, wordsMap]);

  // Find active word based on current time
  const activeWordId = useMemo(() => {
    for (const word of captionData.words) {
      if (word.type === 'silence') continue;
      if (currentTime >= word.start_time && currentTime < word.end_time) {
        return word.id;
      }
    }
    return null;
  }, [captionData.words, currentTime]);

  // Auto-scroll to active word
  useEffect(() => {
    if (activeWordId && activeWordRef.current && containerRef.current) {
      activeWordRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeWordId]);

  // Calculate stats
  const stats = useMemo(() => {
    const wordCount = allWords.filter(({ word }) => 
      word.type !== 'punctuation' && word.type !== 'silence'
    ).length;
    
    const wordsWithTimes = captionData.words.filter(w => 
      w.type !== 'silence' && w.start_time !== undefined && w.end_time !== undefined
    );
    
    if (wordsWithTimes.length === 0) {
      return { wordCount, wpm: 0 };
    }
    
    const firstWord = wordsWithTimes[0];
    const lastWord = wordsWithTimes[wordsWithTimes.length - 1];
    const durationMinutes = (lastWord.end_time - firstWord.start_time) / 60;
    const wpm = durationMinutes > 0 ? Math.round(wordCount / durationMinutes) : 0;
    
    return { wordCount, wpm };
  }, [allWords, captionData.words]);

  const handleWordTap = (word) => {
    if (word.start_time !== undefined) {
      seekToTime(word.start_time);
    }
  };

  // Display mode options
  const displayModes = [
    { id: 'default', label: 'عربي', desc: 'Transliterated' },
    { id: 'code_switched', label: 'Latin', desc: 'Original' },
    { id: 'translated', label: 'مترجم', desc: 'Translated' }
  ];

  // Handle phrase display mode change - apply and save to settings
  const handlePhraseDisplayModeChange = (modeId) => {
    // Apply the mode (this updates captionData)
    applyPhraseDisplayMode(modeId);
    // Save to themeSettings (only this setting, preserving others)
    setThemeSettings(prev => ({
      ...prev,
      phraseDisplayMode: modeId
    }));
  };

  // In Arabizi mode, foreign phrases must stay code_switched (Latin).
  // Apply silently and hide the selector UI.
  useEffect(() => {
    if (!isArabizi || !hasPhrases) return;
    if (phraseDisplayMode === 'code_switched') return;
    handlePhraseDisplayModeChange('code_switched');
  }, [isArabizi, hasPhrases, phraseDisplayMode]);

  return (
    <>
      {/* Foreign Phrases Display Mode Switcher */}
      {hasPhrases && !isArabizi && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('Foreign Words')}</span>
            <span className="text-[10px] text-slate-400">{t('Tap to change style')}</span>
          </div>
          <div className="flex gap-2 p-1 bg-slate-100 rounded-2xl">
            {displayModes.map((mode) => (
              <button
                key={mode.id}
                onClick={() => handlePhraseDisplayModeChange(mode.id)}
                className={`flex-1 px-3 py-2.5 rounded-xl text-center transition-all ${
                  phraseDisplayMode === mode.id
                    ? 'bg-white text-slate-900 shadow-md'
                    : 'text-slate-500'
                }`}
              >
                <div className={`text-sm font-bold ${phraseDisplayMode === mode.id ? 'text-brand' : ''}`}>
                  {t(mode.label)}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">{t(mode.desc)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Arabizi summary: locked phrase behavior */}
      {hasPhrases && isArabizi && (
        <div className="mb-5 p-3 rounded-2xl bg-slate-100 text-slate-600">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('Foreign Words')}</div>
          <div className="text-xs mt-1">
            {t('Locked to Latin (code-switched) in Arabizi mode.')}
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-lg font-bold text-slate-900">{stats.wordCount}</span>
            <span className="text-xs text-slate-400">{t('words')}</span>
          </div>
          <div className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-lg font-bold text-slate-900">{stats.wpm}</span>
            <span className="text-xs text-slate-400">{t('wpm')}</span>
          </div>
        </div>
        <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">
          {t('Read-only')}
        </div>
      </div>

      {/* Prose Container */}
      <div 
        ref={containerRef}
        className={`prose-mobile-container ${isArabizi ? '' : 'rtl'}`}
        style={{ 
          fontFamily: themeSettings.proseFontFamily || 'Noto Naskh Arabic',
          fontSize: `${(themeSettings.proseFontSize || 1.25) * 0.9}rem`,
          lineHeight: 2.2,
          direction: isArabizi ? 'ltr' : 'rtl'
        }}
      >
        {allWords.length === 0 ? (
          <div className="text-center text-slate-400 py-12">{t('No text to display')}</div>
        ) : (
          <p className="text-slate-700 leading-loose">
            {(() => {
              const displayItems = [];
              for (let i = 0; i < allWords.length; i++) {
                const { word } = allWords[i];
                if (word.type === 'punctuation' && word.attaches_to === 'previous') {
                  continue;
                }
                const nextItem = allWords[i + 1];
                const nextWord = nextItem?.word;
                const attachedPunct = (showPunctuation && nextWord?.type === 'punctuation' && nextWord?.attaches_to === 'previous')
                  ? nextWord : null;
                displayItems.push({ word, attachedPunct });
              }

              const getWordText = (item) => `${getDisplayText(item.word)}${item.attachedPunct ? getDisplayText(item.attachedPunct) : ''}`;
              const isLatinItem = (item) => {
                const phrase = getPhraseForWord(item.word.id);
                if (phrase?.activeAlternativeId === 'alt_code_switched') return true;
                const text = getWordText(item);
                const hasLatin = /[A-Za-z]/.test(text);
                const hasArabic = /[\u0600-\u06FF]/.test(text);
                return hasLatin && !hasArabic;
              };

              const renderWord = (item) => {
                const { word, attachedPunct } = item;
                const isActive = word.id === activeWordId;
                const isProtected = !!word.is_protected;
                const phrase = getPhraseForWord(word.id);
                const hasAlternatives = phrase?.alternatives?.length > 0;
                const showPhraseUi = hasAlternatives && !isArabizi;

                // Use consistent sizing to prevent layout shift
                // All words have same padding and border-bottom reserved
                return (
                  <span
                    ref={isActive ? activeWordRef : null}
                    className="inline-block transition-colors duration-150 cursor-pointer border-b-2"
                    style={{
                      padding: '2px 4px',
                      marginBottom: '2px',
                      borderRadius: '4px',
                      backgroundColor: isActive 
                        ? 'rgba(247, 49, 64, 0.15)' 
                        : showPhraseUi 
                          ? 'rgba(139, 92, 246, 0.1)' 
                          : 'transparent',
                      color: isActive 
                        ? '#f73140' 
                        : showPhraseUi 
                          ? '#5b21b6' 
                          : 'inherit',
                      borderBottomColor: showPhraseUi && !isActive
                        ? 'rgba(167, 139, 250, 0.6)'
                        : 'transparent',
                      borderBottomStyle: showPhraseUi && !isActive ? 'dashed' : 'solid',
                      ...(isProtected ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.65 } : {})
                    }}
                    onClick={isProtected ? undefined : () => handleWordTap(word)}
                  >
                    {getDisplayText(word)}{attachedPunct ? getDisplayText(attachedPunct) : ''}
                  </span>
                );
              };

              // Group words by script direction
              const runs = [];
              for (let i = 0; i < displayItems.length; i++) {
                const item = displayItems[i];
                const latin = isLatinItem(item);
                const lastRun = runs[runs.length - 1];
                if (!lastRun || lastRun.isLatin !== latin) {
                  runs.push({ isLatin: latin, items: [item] });
                } else {
                  lastRun.items.push(item);
                }
              }

              return runs.map((run, runIdx) => (
                <React.Fragment key={`${runIdx}-${run.items[0]?.word.id}`}>
                  {run.isLatin ? (
                    <span 
                      dir="ltr" 
                      style={{ 
                        unicodeBidi: 'isolate', 
                        display: 'inline-block',
                        fontFamily: themeSettings.latinFontFamily || 'Inter'
                      }}
                    >
                      {run.items.map((item, idx) => (
                        <React.Fragment key={item.word.id}>
                          {renderWord(item)}
                          {idx < run.items.length - 1 && ' '}
                        </React.Fragment>
                      ))}
                    </span>
                  ) : (
                    run.items.map((item, idx) => (
                      <React.Fragment key={item.word.id}>
                        {renderWord(item)}
                        {idx < run.items.length - 1 && ' '}
                      </React.Fragment>
                    ))
                  )}
                  {runIdx < runs.length - 1 && ' '}
                </React.Fragment>
              ));
            })()}
          </p>
        )}
      </div>
    </>
  );
}

// Inner component that has access to CaptionContext
function EditorPage() {
  const navigate = useNavigate();
  const { id: jobId } = useParams();
  const { 
    updateCaptionData, 
    setVideoSrc, 
    undo,
    redo,
    canUndo,
    canRedo,
    captionData,
    videoSrc,
    getDisplayText,
    themeSettings,
    setThemeSettings,
    reorganizeSkeletons,
    showToast,
    loadFromServer,
    saveToServer,
    setActiveJobId,
    setDisplayMode,
    applyPhraseDisplayMode,
    currentTime,
    displayMode,
    getPhraseForWord,
    seekToTime,
    phraseDisplayMode,
    // Translation
    translationData,
    serverTranslation,
    translationRequested,
    isTranslationView,
    editorReady,
    setEditorReady,
    translationStatus,
    requestTranslation,
    toggleTranslationView,
    setIsTranslationView,
    getCurrentViewData,
    refreshTranslation
  } = useCaptions();
  
  const { user, setUserState } = useAuth();
  const { t } = useI18n();
  const [userPlan, setUserPlan] = useState('free');
  const [trialVideosUsed, setTrialVideosUsed] = useState(0);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [isTrialProtected, setIsTrialProtected] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);

  // Premiere opens this explicit entry point for trial users. Opening the
  // verification modal from the URL also works after a browser login redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verify') === 'phone') setShowVerificationModal(true);
  }, []);
  const [pricingCurrency, setPricingCurrency] = useState(() =>
    getDisplayCurrency({ geoCountry: getCachedCountry() })
  );
  const { offer: welcomeOffer } = useWelcomeOffer();
  const planPrices = getAllPlanPrices(pricingCurrency);
  
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState(null); // 'srt' | 'vtt' | 'txt' | 'json'
  const [nleGuideId, setNleGuideId] = useState(null); // 'premiere' | 'davinci' | 'capcut' | 'finalcut'
  const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);
  const [isRequestingTranslation, setIsRequestingTranslation] = useState(false);
  const [exportSource, setExportSource] = useState('original'); // 'original' or 'translated'
  const translationPollRef = useRef(null);
  const translateMenuRef = useRef(null);
  
  // Local state for export modal chunk settings (separate from themeSettings)
  const [exportChunkSettings, setExportChunkSettings] = useState({
    chunkMode: 'words',
    wordsPerChunk: 3,
    charsPerChunk: 50,
    separateScriptChunks: false,
    silenceAwareChunks: false,
    punctuationAwareChunks: false,
    uppercaseEverything: true
  });
  const [projectName, setProjectName] = useState('Untitled Project');
  const [projectLanguage, setProjectLanguage] = useState('ar'); // ar, en, fr - defaults to Arabic
  const isNonArabicProject = ['en', 'fr', 'english', 'french', 'arabizi'].includes(projectLanguage);
  const shouldUppercaseOutput = isNonArabicProject && themeSettings.uppercaseEverything;
  const formatExportText = useCallback((value) => {
    if (!value || typeof value !== 'string') return value;
    return shouldUppercaseOutput ? value.toLocaleUpperCase() : value;
  }, [shouldUppercaseOutput]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingServer, setIsLoadingServer] = useState(false);
  const [isJobProcessing, setIsJobProcessing] = useState(false);
  const [isJobFailed, setIsJobFailed] = useState(false);
  const [jobError, setJobError] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  // Persist viewMode to localStorage so it remembers the last active tab
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('arabcap_editor_viewMode');
    return saved === 'studio' || saved === 'simple' ? saved : 'simple';
  });
  const [autoSelectWordId, setAutoSelectWordId] = useState(null); // Word to auto-select when switching to studio
  
  // Save viewMode to localStorage whenever it changes
  useEffect(() => {
    if (
      !editorReady &&
      dataLoaded &&
      captionData?.words?.length > 0 &&
      captionData?.skeleton?.length > 0
    ) {
      setEditorReady(true);
    }
  }, [editorReady, dataLoaded, captionData?.words?.length, captionData?.skeleton?.length, captionData?.phrases?.length, setEditorReady]);

  useEffect(() => {
    localStorage.setItem('arabcap_editor_viewMode', viewMode);
  }, [viewMode]);
  const exportMenuRef = useRef(null);
  const confirmResolveRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const hasLoadedFromServerRef = useRef(false);
  const hasLoadedSettingsRef = useRef(false);
  const pendingPhraseModeRef = useRef(null); // Store pending phrase mode to apply once caption data is loaded
  const pendingPhraseModeSourceRef = useRef(null); // 'job' | 'settings' | null
  const pendingInitialChunkSettingsRef = useRef(null); // Store chunk settings to apply on first load
  const [isMobile, setIsMobile] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel'
  });

  // Search functionality
  const requestConfirm = useCallback(({ title, message, confirmLabel, cancelLabel }) => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({
        isOpen: true,
        title,
        message,
        confirmLabel: confirmLabel || 'Confirm',
        cancelLabel: cancelLabel || 'Cancel'
      });
    });
  }, []);

  const closeConfirm = useCallback((result) => {
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
    if (confirmResolveRef.current) {
      confirmResolveRef.current(result);
      confirmResolveRef.current = null;
    }
  }, []);

  const search = useSearch(captionData, updateCaptionData, showToast, requestConfirm);

  // Force save function
  const forceSave = useCallback(async () => {
    if (!jobId || !dataLoaded || isSaving || isTrialProtected) return;
    
    try {
      setIsSaving(true);
      await saveToServer(jobId, captionData, themeSettings, translationData);
      showToast('Saved successfully', 'success');
    } catch (error) {
      showToast(error?.message || 'Failed to save changes', 'warning');
    } finally {
      setIsSaving(false);
    }
  }, [jobId, dataLoaded, isSaving, isTrialProtected, captionData, themeSettings, translationData, saveToServer, showToast]);

  // Keyboard shortcuts (Cmd+F / Ctrl+F for search, Cmd+S / Ctrl+S for save)
  const initialPhraseModeAppliedKey = useMemo(() => {
    if (!jobId) return null;
    return `editor.initialPhraseModeApplied.${jobId}`;
  }, [jobId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+F or Ctrl+F to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        search.openSearch();
      }
      // Cmd+S or Ctrl+S to force save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        forceSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [search, forceSave]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (isJobFailed) {
      // Go back in history, or to dashboard if no history
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isJobFailed, navigate]);

  // Fetch user plan and verification status (only on mount)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const [usageData, verificationStatus, geoCountry, sub] = await Promise.all([
          userService.getUsage(),
          verificationService.getVerificationStatus(),
          detectCountry(),
          subscriptionService.getMySubscription().catch(() => null),
        ]);
        
        if (usageData) {
          setUserPlan(usageData.plan || 'free');
          setTrialVideosUsed(usageData.trial_videos_used || 0);
        }
        
        if (verificationStatus) {
          setPhoneVerified(verificationStatus.phone_verified || false);
        }

        setPricingCurrency(getDisplayCurrency({
          billingCurrency: sub?.billing_currency,
          geoCountry,
        }));
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };
    
    fetchUserData();

    // Listen for verification-required event from API interceptor
    const handleVerificationRequired = async () => {
      try {
        const usageData = await userService.getUsage();
        const plan = usageData?.plan || 'free';
        const isPaidPlan = ['starter', 'pro', 'max'].includes(plan);
        if (!isPaidPlan) {
          setShowVerificationModal(true);
        }
      } catch {
        // If usage check fails, keep existing behavior for safety.
        setShowVerificationModal(true);
      }
    };

    window.addEventListener('verification-required', handleVerificationRequired);
    
    return () => {
      window.removeEventListener('verification-required', handleVerificationRequired);
    };
  }, []);

  // Poll for translation completion when pending
  useEffect(() => {
    if (translationStatus === 'pending' && jobId && !translationPollRef.current) {
      let pollCount = 0;
      const maxPolls = 20; // Poll for max 2 minutes (6s interval)
      
      translationPollRef.current = setInterval(async () => {
        pollCount++;
        const success = await refreshTranslation(jobId);
        
        if (success || pollCount >= maxPolls) {
          if (translationPollRef.current) {
            clearInterval(translationPollRef.current);
            translationPollRef.current = null;
          }
        }
      }, 6000);
    }
    
    return () => {
      if (translationPollRef.current) {
        clearInterval(translationPollRef.current);
        translationPollRef.current = null;
      }
    };
  }, [translationStatus, jobId, refreshTranslation]);

  const handleLoadData = useCallback((data) => {
    if (data.captionData) {
      
      updateCaptionData({
        skeleton: data.captionData.skeleton || [],
        words: data.captionData.words || [],
        phrases: data.captionData.phrases || []
      }, true);

      if (data.mediaUrl) {
        setVideoSrc(data.mediaUrl);
      }

      // Set project name from API (fallback to filename)
      if (data.projectName) {
        setProjectName(data.projectName);
      } else if (data.fileName) {
        setProjectName(data.fileName.replace(/\.[^/.]+$/, ""));
      }

      setDataLoaded(true);
    }
  }, [updateCaptionData, setVideoSrc]);

  useEffect(() => {
    let isMounted = true;
    let pollInterval = null;
    if (!jobId) return;

    const fetchData = async () => {
      try {
        const payload = await loadFromServer(jobId);
        if (!isMounted) return;

        // Check if job failed
        if (payload?.isFailed) {
          setIsJobFailed(true);
          setIsJobProcessing(false);
          setJobError(payload.error || 'Job processing failed');
          setJobStatus('failed');
          if (payload.projectName) {
            setProjectName(payload.projectName);
          } else if (payload.fileName) {
            setProjectName(payload.fileName.replace(/\.[^/.]+$/, ""));
          }
          return true; // Stop polling
        }

        // Check if job is still processing
        if (payload?.isProcessing) {
          setIsJobProcessing(true);
          setIsJobFailed(false);
          setJobStatus(payload.status);
          if (payload.projectName) {
            setProjectName(payload.projectName);
          } else if (payload.fileName) {
            setProjectName(payload.fileName.replace(/\.[^/.]+$/, ""));
          }
          return false; // Not ready yet
        }

        // Job is complete
        setIsJobProcessing(false);
        setIsJobFailed(false);
        setJobStatus(payload?.status || 'completed');

        // Track trial protection status from backend
        setIsTrialProtected(!!payload?.isTrialProtected);
        
        // Set project language (defaults to 'ar' for Arabic)
        if (payload?.language) {
          setProjectLanguage(payload.language);
        }
        const isArabiziJob = payload?.language === 'arabizi';

        if (payload?.captionData) {
          handleLoadData({
            captionData: payload.captionData,
            mediaUrl: payload.mediaUrl,
            fileName: payload.fileName,
            projectName: payload.projectName
          });
          hasLoadedFromServerRef.current = true;
        }
        // Apply job settings FIRST as defaults (from project creation)
        // Then apply editor_settings which override the defaults
        let chunkSettingsToApply = null;
        if (payload?.jobSettings) {
          const js = payload.jobSettings;
          const chunkSettings = {
            chunkMode: js.chunk_mode ?? 'words',
            wordsPerChunk: js.words_per_chunk ?? 3,
            charsPerChunk: js.chars_per_chunk ?? 50,
            separateScriptChunks: js.separate_foreign_chunks ?? false,
            silenceAwareChunks: js.split_on_silence ?? false,
            punctuationAwareChunks: js.split_on_punctuation ?? false
          };
          chunkSettingsToApply = chunkSettings;
          
          setThemeSettings((prev) => ({
            ...prev,
            // Map job settings to theme settings (use != null to handle 0 and false values)
            ...(js.chunk_mode != null && { chunkMode: js.chunk_mode }),
            ...(js.words_per_chunk != null && { wordsPerChunk: js.words_per_chunk }),
            ...(js.chars_per_chunk != null && { charsPerChunk: js.chars_per_chunk }),
            ...(js.split_on_silence != null && { silenceAwareChunks: js.split_on_silence }),
            ...(js.split_on_punctuation != null && { punctuationAwareChunks: js.split_on_punctuation }),
            ...(js.separate_foreign_chunks != null && { separateScriptChunks: js.separate_foreign_chunks }),
          }));
          
          // Apply script style (display mode)
          // 'standard' → 'original', 'arabizi'/'latin' → 'arabizi'
          if (js.script_style) {
            const displayMode = js.script_style === 'arabizi' || js.script_style === 'latin' 
              ? 'arabizi' 
              : 'original';
            setDisplayMode(displayMode);
          }
          
          // Apply foreign words mode (phrase display mode)
          // 'transliterated'/'transliterate' → 'default' (Arabic transliteration)
          // 'code_switched'/'keep' → 'code_switched' (Original Latin)
          // 'translated'/'translate' → 'translated'
          if (js.foreign_words_mode || isArabiziJob) {
            const modeMap = {
              'transliterated': 'default',
              'transliterate': 'default',
              'code_switched': 'code_switched',
              'keep_latin': 'code_switched',
              'keep': 'code_switched',
              'translated': 'translated',
              'translate': 'translated'
            };
            // Arabizi must always use code_switched, regardless of stored job settings.
            const phraseMode = isArabiziJob
              ? 'code_switched'
              : (modeMap[js.foreign_words_mode] || 'default');
            let hasAppliedInitialPhraseMode = false;
            if (initialPhraseModeAppliedKey) {
              try {
                hasAppliedInitialPhraseMode = localStorage.getItem(initialPhraseModeAppliedKey) === 'true';
              } catch (error) {
                hasAppliedInitialPhraseMode = false;
              }
            }
            // Store pending phrase mode only on first display
            if (!hasAppliedInitialPhraseMode) {
              pendingPhraseModeRef.current = phraseMode;
              pendingPhraseModeSourceRef.current = 'job';
              setThemeSettings(prev => ({
                ...prev,
                phraseDisplayMode: phraseMode
              }));
            }
          }
        }
        // Apply editor_settings AFTER job settings - these are user's saved changes that override defaults
        if (payload?.settings) {
          setThemeSettings((prev) => ({ ...prev, ...payload.settings }));
          // If editor_settings has chunk settings, use those instead
          if (payload.settings.wordsPerChunk != null || payload.settings.chunkMode != null) {
            chunkSettingsToApply = {
              chunkMode: payload.settings.chunkMode ?? chunkSettingsToApply?.chunkMode ?? 'words',
              wordsPerChunk: payload.settings.wordsPerChunk ?? chunkSettingsToApply?.wordsPerChunk ?? 3,
              charsPerChunk: payload.settings.charsPerChunk ?? chunkSettingsToApply?.charsPerChunk ?? 50,
              separateScriptChunks: payload.settings.separateScriptChunks ?? chunkSettingsToApply?.separateScriptChunks ?? false,
              silenceAwareChunks: payload.settings.silenceAwareChunks ?? chunkSettingsToApply?.silenceAwareChunks ?? false,
              punctuationAwareChunks: payload.settings.punctuationAwareChunks ?? chunkSettingsToApply?.punctuationAwareChunks ?? false
            };
          }
          // If editor_settings has phraseDisplayMode (set from mobile), override the job settings phrase mode
          if (payload.settings.phraseDisplayMode || isArabiziJob) {
            // Arabizi must always use code_switched, regardless of saved editor settings.
            const forcedPhraseMode = isArabiziJob
              ? 'code_switched'
              : payload.settings.phraseDisplayMode;
            pendingPhraseModeRef.current = forcedPhraseMode;
            pendingPhraseModeSourceRef.current = 'settings';
            setThemeSettings(prev => ({
              ...prev,
              phraseDisplayMode: forcedPhraseMode
            }));
          }
        }
        // Store chunk settings for initial reorganization
        if (chunkSettingsToApply) {
          pendingInitialChunkSettingsRef.current = chunkSettingsToApply;
        }
        hasLoadedSettingsRef.current = true;
        return true; // Data loaded
      } catch (error) {
        if (!isMounted) return true; // Stop polling on error
        showToast(error?.message || 'Failed to load editor data', 'warning');
        return true; // Stop polling on error
      }
    };

    setActiveJobId(jobId);
    setIsLoadingServer(true);

    fetchData().then((isReady) => {
      if (!isMounted) return;
      setIsLoadingServer(false);

      // If job is still processing, poll every 3 seconds
      if (!isReady) {
        pollInterval = setInterval(async () => {
          const ready = await fetchData();
          if (ready && pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }, 3000);
      }
    });

    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [jobId, handleLoadData, loadFromServer, setActiveJobId, setThemeSettings, showToast]);

  // Apply pending phrase mode once caption data with phrases is loaded
  useEffect(() => {
    
    if (
      pendingPhraseModeRef.current && 
      captionData?.phrases && 
      captionData.phrases.length > 0 &&
      dataLoaded &&
      editorReady
    ) {
      // Apply the pending phrase mode with forceTransform=true for initial load
      // This ensures code-switched alternatives are actually applied even if already set
      applyPhraseDisplayMode(pendingPhraseModeRef.current, true);
      if (pendingPhraseModeSourceRef.current === 'job') {
        setThemeSettings(prev => ({
          ...prev,
          phraseDisplayMode: pendingPhraseModeRef.current
        }));
      }
      if (pendingPhraseModeSourceRef.current === 'job' && initialPhraseModeAppliedKey) {
        try {
          localStorage.setItem(initialPhraseModeAppliedKey, 'true');
        } catch (error) {
          // Ignore localStorage errors (privacy mode, etc.)
        }
      }
      // Clear the pending mode so it's not applied again
      pendingPhraseModeRef.current = null;
      pendingPhraseModeSourceRef.current = null;
    }
  }, [captionData?.phrases, dataLoaded, editorReady, applyPhraseDisplayMode, initialPhraseModeAppliedKey]);

  // Apply initial chunk settings (reorganize) once caption data is loaded
  useEffect(() => {
    if (
      pendingInitialChunkSettingsRef.current && 
      captionData?.skeleton && 
      captionData.skeleton.length > 0 &&
      captionData?.words &&
      captionData.words.length > 0 &&
      dataLoaded &&
      hasLoadedSettingsRef.current
    ) {
      const settings = pendingInitialChunkSettingsRef.current;
      // Apply initial reorganization with the loaded settings
      reorganizeSkeletons({
        mode: settings.chunkMode || 'words',
        maxWords: settings.wordsPerChunk,
        maxChars: settings.charsPerChunk,
        separateScripts: settings.separateScriptChunks,
        silenceAware: settings.silenceAwareChunks,
        punctuationAware: settings.punctuationAwareChunks
      });
      // Clear so it doesn't run again
      pendingInitialChunkSettingsRef.current = null;
    }
  }, [captionData?.skeleton, captionData?.words, dataLoaded, reorganizeSkeletons]);

  useEffect(() => {
    // Don't autosave until both data and settings are loaded from server
    if (!jobId || !dataLoaded || !hasLoadedFromServerRef.current || !hasLoadedSettingsRef.current) return;

    // Never auto-save when content is trial-protected (scrambled words would overwrite real data)
    if (isTrialProtected) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        if (!themeSettings?.phraseDisplayMode) {
          setIsSaving(false);
          return;
        }
        // Auto-save includes translation data
        await saveToServer(jobId, captionData, themeSettings, translationData);
      } catch (error) {
        showToast(error?.message || 'Failed to save changes', 'warning');
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [captionData, dataLoaded, jobId, isTrialProtected, saveToServer, showToast, themeSettings, translationData]);

  // Keep themeSettings phraseDisplayMode in sync so it persists to server
  useEffect(() => {
    if (!phraseDisplayMode) return;
    setThemeSettings(prev => {
      if (prev?.phraseDisplayMode === phraseDisplayMode) return prev;
      return { ...prev, phraseDisplayMode };
    });
  }, [phraseDisplayMode, jobId, setThemeSettings]);

  const handleExportJson = useCallback(() => {
    // Get the data based on export source
    const dataSource = exportSource === 'translated' && translationData 
      ? translationData.words 
      : captionData.words;
    const protectedIds = new Set(
      (captionData.words || []).filter((w) => w?.is_protected).map((w) => w.id)
    );
    
    // Sort words by start time and filter out silences (and protected words)
    const sortedWords = [...dataSource]
      .filter(w => w.type !== 'silence' && !w.is_protected && !protectedIds.has(w.id))
      .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));
    
    // Build payload with punctuation attached to previous word
    const payload = [];
    for (let i = 0; i < sortedWords.length; i++) {
      const word = sortedWords[i];
      
      // Skip punctuation - it will be attached to previous word
      if (word.type === 'punctuation' && word.attaches_to === 'previous') {
        continue;
      }
      
      // Get the word text - for translated, use text directly; for original, use displayText
      let wordText = exportSource === 'translated' ? word.text : getDisplayText(word);
      wordText = formatExportText(wordText);
      
      // Check if next word is punctuation that attaches to this word
      const nextWord = sortedWords[i + 1];
      if (nextWord?.type === 'punctuation' && nextWord?.attaches_to === 'previous') {
        const punctText = exportSource === 'translated' ? nextWord.text : getDisplayText(nextWord);
        wordText += formatExportText(punctText);
      }
      
      payload.push({
        word: wordText,
        start: word.start_time ?? 0,
        end: word.end_time ?? 0
      });
    }
    
    const sourceLabel = exportSource === 'translated' ? ' (English)' : '';
    const dataStr = JSON.stringify(payload, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Arabcap - ${projectName || 'Untitled'}${sourceLabel}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [captionData.words, translationData, exportSource, getDisplayText, projectName]);

  const formatSrtTime = (time) => {
    const totalMs = Math.max(0, Math.round((time || 0) * 1000));
    const hours = Math.floor(totalMs / 3600000);
    const minutes = Math.floor((totalMs % 3600000) / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
  };

  const formatVttTime = (time) => {
    const totalMs = Math.max(0, Math.round((time || 0) * 1000));
    const hours = Math.floor(totalMs / 3600000);
    const minutes = Math.floor((totalMs % 3600000) / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');
    const mmm = ms.toString().padStart(3, '0');
    return hours > 0
      ? `${hours.toString().padStart(2, '0')}:${mm}:${ss}.${mmm}`
      : `${mm}:${ss}.${mmm}`;
  };

  // IDs locked behind trial blur — always taken from original caption data.
  const getProtectedWordIds = useCallback(() => {
    const ids = new Set();
    (captionData.words || []).forEach((w) => {
      if (w?.is_protected) ids.add(w.id);
    });
    return ids;
  }, [captionData.words]);

  // Get the data source based on export source selection.
  // Disabled skeletons are excluded so export matches the active Studio layout.
  // Trial-locked (is_protected) words are stripped so downloads only include
  // the unlocked ~50% the user can actually see.
  const getExportData = useCallback(() => {
    const protectedIds = getProtectedWordIds();
    const enforceTrialCut = isTrialProtected || protectedIds.size > 0;

    const source =
      exportSource === 'translated' && translationData
        ? {
            skeleton: translationData.skeleton || [],
            words: translationData.words || []
          }
        : {
            skeleton: captionData.skeleton || [],
            words: captionData.words || []
          };

    const wordsById = new Map((source.words || []).map((w) => [w.id, w]));

    const skeleton = [];
    for (const seg of source.skeleton || []) {
      if (seg.disabled) continue;

      let wordIds = seg.words || [];
      if (enforceTrialCut) {
        wordIds = wordIds.filter((id) => !protectedIds.has(id));
      }
      if (wordIds.length === 0) continue;

      // Drop cues that have no remaining content after the trial cut
      // (silence/punctuation-only leftovers should not become SRT lines).
      const hasContent = wordIds.some((id) => {
        const w = wordsById.get(id);
        return w && w.type !== 'silence' && w.type !== 'punctuation' && !w.is_protected;
      });
      if (!hasContent) continue;

      let startTime = seg.start_time;
      let endTime = seg.end_time;

      // When a cue sits on the 50% boundary, trim timing to the unlocked words only.
      if (enforceTrialCut) {
        const unlocked = wordIds
          .map((id) => wordsById.get(id))
          .filter((w) => w && w.type !== 'silence');
        if (unlocked.length > 0) {
          const starts = unlocked
            .map((w) => w.start_time)
            .filter((t) => typeof t === 'number');
          const ends = unlocked
            .map((w) => w.end_time)
            .filter((t) => typeof t === 'number');
          if (starts.length > 0) startTime = Math.min(...starts);
          if (ends.length > 0) endTime = Math.max(...ends);
        }
      }

      skeleton.push({
        ...seg,
        words: wordIds,
        start_time: startTime,
        end_time: endTime
      });
    }

    const words = enforceTrialCut
      ? (source.words || []).filter((w) => !protectedIds.has(w.id) && !w.is_protected)
      : source.words || [];

    return { skeleton, words };
  }, [
    exportSource,
    translationData,
    captionData,
    getProtectedWordIds,
    isTrialProtected
  ]);

  // Build cue text to match Studio: same words, punctuation attachment, and line breaks.
  const buildCueText = (skeleton, sourceData = null) => {
    const data = sourceData || getExportData();
    // Defense in depth: never export trial-locked / protected words.
    const protectedIds = getProtectedWordIds();
    const words = skeleton.words
      .map(id => data.words.find(w => w.id === id))
      .filter(w => w && !w.is_protected && !protectedIds.has(w.id));

    const showPunctuation = themeSettings.showPunctuation !== false;
    const enableAutoLineBreak = !!themeSettings.enableAutoLineBreak;
    const wordsPerLine = themeSettings.wordsPerLine || 4;
    const useTranslated = exportSource === 'translated';

    const tokensWithBreaks = [];
    let pendingPrefix = '';

    for (const word of words) {
      if (word.type === 'silence') continue;

      if (word.type === 'punctuation') {
        if (!showPunctuation) continue;
        const punct = formatExportText(useTranslated ? word.text : getDisplayText(word));
        if (word.attaches_to === 'previous' && tokensWithBreaks.length > 0) {
          tokensWithBreaks[tokensWithBreaks.length - 1].text += punct;
        } else if (word.attaches_to === 'next') {
          pendingPrefix += punct;
        } else {
          tokensWithBreaks.push({ text: punct, breakAfter: false });
        }
        continue;
      }

      const text = `${pendingPrefix}${formatExportText(useTranslated ? word.text : getDisplayText(word))}`;
      pendingPrefix = '';
      tokensWithBreaks.push({
        text,
        breakAfter: word.break === true,
      });
    }

    if (pendingPrefix && tokensWithBreaks.length > 0) {
      tokensWithBreaks[tokensWithBreaks.length - 1].text += pendingPrefix;
    }

    if (tokensWithBreaks.length === 0) return '';

    // Insert newlines to match Studio manual breaks + auto line breaks
    const lines = [];
    let currentLine = [];
    tokensWithBreaks.forEach((token, index) => {
      currentLine.push(token.text);
      const isLast = index >= tokensWithBreaks.length - 1;
      const showManualBreak = token.breakAfter && !isLast;
      const showAutoBreak =
        !token.breakAfter &&
        enableAutoLineBreak &&
        !isLast &&
        index === wordsPerLine - 1;

      if (showManualBreak || showAutoBreak) {
        lines.push(currentLine.join(' '));
        currentLine = [];
      }
    });
    if (currentLine.length > 0) {
      lines.push(currentLine.join(' '));
    }

    return lines.join('\n');
  };

  const buildPlainText = (sourceData = null) => {
    const data = sourceData || getExportData();
    const protectedIds = getProtectedWordIds();
    const words = [...data.words]
      .filter(w => w.type !== 'silence' && !w.is_protected && !protectedIds.has(w.id))
      .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));

    const tokens = [];
    let pendingPrefix = '';
    const showPunctuation = themeSettings.showPunctuation !== false;

    for (const word of words) {
      if (word.type === 'punctuation') {
        if (!showPunctuation) continue;
        const punct = formatExportText(exportSource === 'translated' ? word.text : getDisplayText(word));
        if (word.attaches_to === 'previous' && tokens.length > 0) {
          tokens[tokens.length - 1] += punct;
        } else if (word.attaches_to === 'next') {
          pendingPrefix += punct;
        } else {
          tokens.push(punct);
        }
        continue;
      }

      const text = `${pendingPrefix}${formatExportText(exportSource === 'translated' ? word.text : getDisplayText(word))}`;
      pendingPrefix = '';
      tokens.push(text);
    }

    if (pendingPrefix && tokens.length > 0) {
      tokens[tokens.length - 1] += pendingPrefix;
    }

    return tokens.join(' ');
  };

  const downloadFile = (content, filename, type = 'text/plain') => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async (format) => {
    if (!jobId) {
      showToast('Unable to export: missing job ID', 'warning');
      return { success: false, needsVerification: false };
    }

    // Trial users must verify their phone before exporting anything (no partial export).
    const needsVerification = await isVerificationRequiredForExport();
    if (needsVerification) {
      setIsExportOpen(false);
      setIsMobileExportOpen(false);
      setShowVerificationModal(true);
      return { success: false, needsVerification: true };
    }

    // The backend is the authority: it rejects unverified trial exports with a 403.
    try {
      await editorService.exportCaptions(jobId, format, {
        showPunctuation: themeSettings.showPunctuation !== false,
      });
    } catch (trackErr) {
      const detail = trackErr?.response?.data?.detail;
      if (trackErr?.response?.status === 403 && typeof detail === 'string' &&
          detail.toLowerCase().includes('verification required')) {
        setIsExportOpen(false);
        setIsMobileExportOpen(false);
        setShowVerificationModal(true);
        return { success: false, needsVerification: true };
      }
      console.warn('Export tracking failed:', trackErr);
    }

    try {
      // Persist latest Studio state (best-effort) so saves stay in sync,
      // but always build the download from live in-memory Studio layout.
      if (!isTrialProtected) {
        try {
          await saveToServer(jobId, captionData, themeSettings, translationData);
        } catch (saveErr) {
          console.warn('Pre-export save failed:', saveErr);
        }
      }

      if (format === 'json') {
        handleExportJson();
      } else {
        const data = getExportData();
        const baseName = projectName || 'transcript';
        const sourceLabel =
          exportSource === 'translated' && translationData ? ' (English)' : '';

        if (format === 'txt') {
          const content = buildPlainText(data);
          downloadFile(content, `${baseName}${sourceLabel}.txt`);
        } else if (format === 'srt') {
          const lines = [];
          let cueIdx = 1;
          data.skeleton.forEach((seg) => {
            const text = buildCueText(seg, data);
            if (!text) return;
            lines.push(`${cueIdx++}`);
            lines.push(`${formatSrtTime(seg.start_time)} --> ${formatSrtTime(seg.end_time)}`);
            lines.push(text);
            lines.push('');
          });
          downloadFile(lines.join('\n'), `${baseName}${sourceLabel}.srt`);
        } else if (format === 'vtt') {
          const lines = ['WEBVTT', ''];
          data.skeleton.forEach((seg) => {
            const text = buildCueText(seg, data);
            if (!text) return;
            lines.push(`${formatVttTime(seg.start_time)} --> ${formatVttTime(seg.end_time)}`);
            lines.push(text);
            lines.push('');
          });
          downloadFile(lines.join('\n'), `${baseName}${sourceLabel}.vtt`);
        } else {
          showToast('Unsupported export format', 'warning');
          return { success: false, needsVerification: false };
        }
      }

      setIsExportOpen(false);
      setIsMobileExportOpen(false);
      setExportFormat(null);

      return { success: true, needsVerification: false };
    } catch (error) {
      showToast(error?.response?.data?.detail || 'Export failed. Please try again.', 'warning');
      return { success: false, needsVerification: false };
    }
  };

  const handleNleExport = async (nleId) => {
    const result = await handleExport('srt');
    if (result?.success && !result.needsVerification) {
      setIsMobileExportOpen(false);
      setNleGuideId(nleId);
    }
  };

  const handleExportSettingChange = (key, value) => {
    setExportChunkSettings(prev => ({ ...prev, [key]: value }));
  };

  // Handle translation request
  const handleRequestTranslation = async (language = 'en') => {
    if (!jobId || isRequestingTranslation) return;
    
    setIsRequestingTranslation(true);
    try {
      const result = await requestTranslation(jobId, language);
      if (result.success) {
        setIsTranslateModalOpen(false);
      }
    } finally {
      setIsRequestingTranslation(false);
    }
  };

  const isVerificationRequiredForExport = async () => {
    try {
      const [usageData, verificationStatus] = await Promise.all([
        userService.getUsage(),
        verificationService.getVerificationStatus()
      ]);

      if (usageData) {
        setUserPlan(usageData.plan || 'free');
        setTrialVideosUsed(usageData.trial_videos_used || 0);
      }

      if (verificationStatus) {
        setPhoneVerified(verificationStatus.phone_verified || false);
      }

      const plan = usageData?.plan || userPlan;
      if (['starter', 'pro', 'max'].includes(plan)) {
        return false;
      }
      // Every trial export requires a verified phone number.
      return !(verificationStatus?.phone_verified || false);
    } catch (error) {
      console.error('Failed to check verification status:', error);
      // Fall back to what the editor already knows; the backend still enforces it.
      return isTrialProtected;
    }
  };

  const handleVerificationComplete = async () => {
    setPhoneVerified(true);
    setShowVerificationModal(false);
    setIsTrialProtected(false);

    // Re-fetch editor data so the full (unprotected) content loads
    if (jobId) {
      try {
        const payload = await loadFromServer(jobId);
        if (payload?.captionData) {
          handleLoadData({
            captionData: payload.captionData,
            mediaUrl: payload.mediaUrl,
            fileName: payload.fileName,
            projectName: payload.projectName
          });
        }
        setIsTrialProtected(!!payload?.isTrialProtected);
      } catch (error) {
        console.error('Failed to reload editor data after verification:', error);
      }
    }

    if (isMobile) {
      setIsMobileExportOpen(true);
    } else {
      setIsExportOpen(true);
    }
  };

  const handleUpgradeClick = () => {
    setShowVerificationModal(false);
    setBillingError(null);
    setShowUpgradeModal(true);
  };

  const handleUpgradeToPlan = async (plan) => {
    try {
      setUpgradeLoading(true);
      setBillingError(null);

      if (userPlan === 'starter' || userPlan === 'pro' || userPlan === 'max') {
        const result = await subscriptionService.openPortal();
        if (result?.portal_url) {
          window.location.href = result.portal_url;
        } else {
          setBillingError('Failed to open billing portal');
        }
      } else {
        const result = await subscriptionService.createCheckoutSession(plan, pricingCurrency, welcomeOffer?.plans.includes(plan) ? welcomeOffer.percent_off : null);
        if (result?.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          setBillingError('Failed to create checkout session');
        }
      }
    } catch (error) {
      console.error('Failed to process upgrade:', error);
      setBillingError('Failed to process upgrade. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleExportClick = async () => {
    // Opening the menu is fine; handleExport() requires phone verification before any download.
    setIsExportOpen((prev) => {
      if (prev) {
        setExportFormat(null);
      }
      return !prev;
    });
  };

  const handleMobileExportFormat = async (format) => {
    await handleExport(format);
    setIsMobileExportOpen(false);
  };

  const handleApplyExportChunking = () => {
    // Apply export chunk settings to themeSettings and reorganize
    setThemeSettings(prev => ({
      ...prev,
      chunkMode: exportChunkSettings.chunkMode,
      wordsPerChunk: exportChunkSettings.wordsPerChunk,
      charsPerChunk: exportChunkSettings.charsPerChunk,
      separateScriptChunks: exportChunkSettings.separateScriptChunks,
      silenceAwareChunks: exportChunkSettings.silenceAwareChunks,
      punctuationAwareChunks: exportChunkSettings.punctuationAwareChunks,
      uppercaseEverything: exportChunkSettings.uppercaseEverything
    }));
    reorganizeSkeletons({
      mode: exportChunkSettings.chunkMode || 'words',
      maxWords: exportChunkSettings.wordsPerChunk,
      maxChars: exportChunkSettings.charsPerChunk,
      separateScripts: exportChunkSettings.separateScriptChunks,
      silenceAware: exportChunkSettings.silenceAwareChunks,
      punctuationAware: exportChunkSettings.punctuationAwareChunks
    });
  };

  // Sync export chunk settings with themeSettings when export modal opens
  useEffect(() => {
    if (isExportOpen) {
      setExportChunkSettings({
        chunkMode: themeSettings.chunkMode || 'words',
        wordsPerChunk: themeSettings.wordsPerChunk || 3,
        charsPerChunk: themeSettings.charsPerChunk || 50,
        separateScriptChunks: themeSettings.separateScriptChunks || false,
        silenceAwareChunks: themeSettings.silenceAwareChunks || false,
        punctuationAwareChunks: themeSettings.punctuationAwareChunks || false,
        uppercaseEverything: themeSettings.uppercaseEverything || false
      });
    }
  }, [isExportOpen]);

  // Close translate modal on click outside
  useEffect(() => {
    if (!isTranslateModalOpen) return;
    const handleClickOutside = (e) => {
      if (translateMenuRef.current && !translateMenuRef.current.contains(e.target)) {
        setIsTranslateModalOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTranslateModalOpen]);

  useEffect(() => {
    if (!isExportOpen) return;
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setIsExportOpen(false);
        setExportFormat(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExportOpen]);

  // Auto-start editor tour for first-time users (after data is loaded)
  useEffect(() => {
    if (dataLoaded && shouldShowEditorTour()) {
      // Small delay to let the UI settle
      const timer = setTimeout(() => setShowEditorTour(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [dataLoaded]);

  const handleBack = async () => {
    const goBack = () => {
      // Use browser history if available, otherwise go to dashboard
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard', { replace: true });
      }
    };

    // If we have a jobId (server-synced) and not currently saving, just go back
    if (jobId && !isSaving) {
      goBack();
      return;
    }
    
    // Otherwise, ask for confirmation
    const confirmed = await requestConfirm({
      title: 'Leave the editor?',
      message: 'Unsaved changes will be lost.',
      confirmLabel: 'Leave',
      cancelLabel: 'Stay'
    });
    if (confirmed) {
      goBack();
    }
  };

  // Mobile export modal state
  const [isMobileExportOpen, setIsMobileExportOpen] = useState(false);
  
  // Editor tour state
  const [showEditorTour, setShowEditorTour] = useState(false);
  
  // Settings tour state
  const [showSettingsTour, setShowSettingsTour] = useState(false);
  const hasShownSettingsTourRef = useRef(false);

  if (isMobile) {
    // Show skeleton loading when job is still being transcribed or loading
    if (isJobProcessing || isLoadingServer) {
      return <EditorSkeleton />;
    }

    // Show simplified mobile view with read-only prose
    return (
      <div className="flex flex-col h-[calc(100dvh-var(--welcome-banner-height,0px))] font-sans overflow-hidden relative transition-colors duration-300 bg-surface-100 text-slate-900">
        {/* Background Grid Pattern */}
        <div 
          className="absolute inset-0 z-0 pointer-events-none opacity-40"
          style={{ 
            backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', 
            backgroundSize: '24px 24px' 
          }} 
        />

        {/* Mobile Header */}
        <header className="h-14 px-4 backdrop-blur-md border-b flex items-center justify-between shrink-0 z-30 relative bg-white/80 border-slate-100">
          <div className="flex items-center gap-3">
            <button 
              onClick={handleBack}
              className="p-2 rounded-full transition-colors hover:bg-slate-50 text-slate-400 hover:text-slate-900"
            >
              <ChevronLeftIcon />
            </button>
            
            <div className="flex flex-col">
              <span className="text-sm font-bold text-slate-900 truncate max-w-[140px]">
                {projectName}
              </span>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${isSaving ? 'bg-amber-500' : 'bg-emerald-500'}`}></span>
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  {t(isSaving ? 'Saving...' : 'Saved')}
                </span>
              </div>
            </div>
          </div>

          {/* Export Button */}
          <button 
            onClick={() => setIsMobileExportOpen(true)}
            className="h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-full shadow-lg shadow-slate-900/10 flex items-center gap-2 transition-all active:scale-[0.97]"
          >
            <DownloadIcon />
            {t('Export')}
          </button>
        </header>

        {/* Read-only Banner */}
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center justify-center gap-2 shrink-0 z-20">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-600">
            <rect x="3" y="4" width="18" height="14" rx="2" />
            <path d="M8 20h8" />
            <path d="M12 18v2" />
          </svg>
          <span className="text-xs font-medium text-amber-800">
            {t('Read-only mode · Open your computer to edit')}
          </span>
        </div>

        {/* Trial Protection Banner (mobile) */}
        {isTrialProtected && (
          <div className="border-b border-brand/10 shrink-0 z-20" style={{ backgroundColor: '#fff0f1' }}>
            <div className="py-3 flex items-center justify-center gap-3 px-4">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand flex items-center justify-center">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <p className="text-xs font-semibold text-slate-700 flex-1">
                {t('Viewing 50% of transcript')}
                <span className="text-slate-400 mx-1">·</span>
                <span className="text-slate-500 font-normal">{t('Verify to unlock')}</span>
              </p>
              <button
                onClick={() => setShowVerificationModal(true)}
                className="flex-shrink-0 px-3.5 py-1.5 bg-brand hover:bg-brand-dark text-white text-xs font-bold rounded-lg transition-all active:scale-[0.98]"
              >
                {t('Verify')}
              </button>
            </div>
          </div>
        )}

        {dataLoaded && captionData.skeleton.length > 0 ? (
          <>
            {/* Main Content Area - Scrollable */}
            <div className="flex-1 overflow-y-auto scroll-smooth relative z-10" id="editor-scroll">
              <div className="mx-auto px-4 pt-6 pb-24 max-w-full">
                {/* Read-only Prose View */}
                <MobileProseView 
                  captionData={captionData}
                  currentTime={currentTime}
                  themeSettings={themeSettings}
                  getDisplayText={getDisplayText}
                  displayMode={displayMode}
                  getPhraseForWord={getPhraseForWord}
                  seekToTime={seekToTime}
                  phraseDisplayMode={phraseDisplayMode}
                  applyPhraseDisplayMode={applyPhraseDisplayMode}
                  hasPhrases={captionData?.phrases?.length > 0}
                  setThemeSettings={setThemeSettings}
                />
              </div>
            </div>

            {/* Floating Audio Player */}
            {videoSrc && <VideoPreview />}
          </>
        ) : (
          /* Skeleton Loading */
          <EditorContentSkeleton />
        )}

        {/* Mobile Export Modal */}
        {isMobileExportOpen && (
          <div className="fixed inset-0 z-[100] flex items-end justify-center">
            <div 
              className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" 
              onClick={() => setIsMobileExportOpen(false)} 
            />
            <div className="relative bg-white rounded-t-3xl w-full max-w-lg p-6 pb-8 shadow-2xl border-t border-slate-100 animate-[slideUp_0.3s_ease-out]">
              {/* Warning Banner */}
              <div className="mb-5 p-4 rounded-2xl bg-amber-50 border border-amber-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-600">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-900">{t('Review before exporting')}</p>
                    <p className="text-xs text-amber-700 mt-1">
                      {t('We recommend editing your transcript on a computer first to ensure accuracy before exporting.')}
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-bold text-slate-900 mb-4">{t('Export transcript')}</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="px-4 py-4 rounded-2xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all active:scale-[0.98] flex flex-col items-center gap-1"
                  onClick={() => handleMobileExportFormat('srt')}
                >
                  <span className="text-lg">📄</span>
                  SRT
                </button>
                {/* VTT export temporarily disabled
                <button
                  className="px-4 py-4 rounded-2xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all active:scale-[0.98] flex flex-col items-center gap-1"
                  onClick={() => handleMobileExportFormat('vtt')}
                >
                  <span className="text-lg">📄</span>
                  VTT
                </button>
                */}
                <button
                  className="px-4 py-4 rounded-2xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all active:scale-[0.98] flex flex-col items-center gap-1"
                  onClick={() => handleMobileExportFormat('txt')}
                >
                  <span className="text-lg">📝</span>
                  TXT
                </button>
                {/* JSON export temporarily disabled
                <button
                  className="px-4 py-4 rounded-2xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all active:scale-[0.98] flex flex-col items-center gap-1"
                  onClick={() => handleMobileExportFormat('json')}
                >
                  <span className="text-lg">{ }</span>
                  JSON
                </button>
                */}
              </div>

              <div className="mt-5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('Export for editor')}</p>
                <div className="grid grid-cols-2 gap-3">
                  {Object.values(NLE_EXPORT_GUIDES).map((guide) => {
                    const Logo = guide.Logo;
                    return (
                      <button
                        key={guide.id}
                        className="px-3 py-3 rounded-2xl text-xs font-bold bg-slate-50 border border-slate-200 text-slate-800 hover:bg-white hover:border-slate-300 transition-all active:scale-[0.98] flex flex-col items-center gap-2"
                        onClick={() => handleNleExport(guide.id)}
                      >
                        <Logo size={32} />
                        <span className="text-center leading-tight">{guide.name}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                  {t('Downloads an SRT and shows steps to import it into that app.')}
                </p>
              </div>

              <button
                onClick={() => setIsMobileExportOpen(false)}
                className="mt-5 w-full px-4 py-3 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-50 transition-colors"
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Upgrade Modal (mobile) */}
        {showUpgradeModal && (
          <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-[#f5f5f5] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-4xl max-h-[92vh] overflow-y-auto relative">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="absolute top-3 end-3 z-10 w-7 h-7 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 flex items-center justify-center transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              <div className="text-center pt-6 pb-3 px-4">
                <h2 className="text-2xl font-black text-slate-900">{t('Our Pricing Options')}</h2>
              </div>

              <div className="px-3 pb-6">
                <div className="grid grid-cols-1 gap-3">
                  <div className="bg-white rounded-xl p-4 shadow-sm flex flex-col">
                    <div className="mb-3">
                      <h3 className="text-lg font-black text-slate-900 mb-1">STARTER</h3>
                      <p className="text-xs text-slate-500">{t('Perfect for beginner video editors who are just getting started.')}</p>
                    </div>
                    <div className="text-center mb-3">
                      <span className="text-3xl font-black text-slate-900">{t(planPrices.starter.display)}</span>
                      <span className="text-slate-500 text-xs">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('starter')}
                      disabled={upgradeLoading || userPlan === 'starter'}
                      className={`w-full py-3 rounded-full font-bold text-xs transition-all ${
                        userPlan === 'starter'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'starter' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GET STARTED')}
                    </button>
                  </div>

                  <div className="bg-[#f73140] rounded-xl p-4 shadow-lg flex flex-col relative">
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 bg-slate-900 text-white text-[10px] font-bold rounded-full uppercase tracking-wide">{t('Popular')}</span>
                    </div>
                    <div className="mb-3 mt-2">
                      <h3 className="text-lg font-black text-white mb-1">PRO</h3>
                      <p className="text-xs text-white/80">{t('Great for active editors working with more clients and content.')}</p>
                    </div>
                    <div className="text-center mb-3">
                      <span className="text-3xl font-black text-white">{<WelcomePrice plan="pro" price={planPrices.pro} />}</span>
                      <span className="text-white/80 text-xs">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('pro')}
                      disabled={upgradeLoading || userPlan === 'pro'}
                      className={`w-full py-3 rounded-full font-bold text-xs transition-all ${
                        userPlan === 'pro'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-white text-[#f73140] hover:bg-slate-50 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'pro' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GO PRO')}
                    </button>
                  </div>

                  <div className="bg-white rounded-xl p-4 shadow-sm flex flex-col">
                    <div className="mb-3">
                      <h3 className="text-lg font-black text-slate-900 mb-1">MAX</h3>
                      <p className="text-xs text-slate-500">{t('Built for professional editors handling large content demands.')}</p>
                    </div>
                    <div className="text-center mb-3">
                      <span className="text-3xl font-black text-slate-900">{<WelcomePrice plan="max" price={planPrices.max} />}</span>
                      <span className="text-slate-500 text-xs">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('max')}
                      disabled={upgradeLoading || userPlan === 'max'}
                      className={`w-full py-3 rounded-full font-bold text-xs transition-all ${
                        userPlan === 'max'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'max' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GET PREMIUM')}
                    </button>
                  </div>
                </div>

                {billingError && (
                  <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-100">
                    <p className="text-xs text-red-600 text-center">{t(billingError)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <PhoneVerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onVerified={handleVerificationComplete}
          onUpgrade={handleUpgradeClick}
          phoneNumber={user?.phone_number}
        />

        {nleGuideId && NLE_EXPORT_GUIDES[nleGuideId] && (() => {
          const guide = NLE_EXPORT_GUIDES[nleGuideId];
          const Logo = guide.Logo;
          return (
            <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto relative m-0 sm:m-4">
                <button
                  onClick={() => setNleGuideId(null)}
                  className="absolute top-3 end-3 z-10 w-8 h-8 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-colors"
                  aria-label={t('Close')}
                >
                  <XIcon />
                </button>
                <div className="p-6 pt-8">
                  <div className="flex items-center gap-3 mb-2">
                    <Logo size={40} />
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t('SRT downloaded')}</p>
                      <h3 className="text-xl font-black text-slate-900">{t('Import into {app}', { app: guide.name })}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mb-5">
                    {t('Follow these steps to add your subtitles in {app}.', { app: guide.name })}
                  </p>
                  <ol className="space-y-3">
                    {guide.steps.map((step, index) => (
                      <li key={index} className="flex gap-3">
                        <span
                          className="shrink-0 w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center mt-0.5"
                          style={{ backgroundColor: guide.accent }}
                        >
                          {index + 1}
                        </span>
                        <p className="text-sm text-slate-700 leading-relaxed">{t(step)}</p>
                      </li>
                    ))}
                  </ol>
                  <button
                    onClick={() => setNleGuideId(null)}
                    className="mt-6 w-full px-4 py-3 rounded-xl text-sm font-bold text-white transition-colors"
                    style={{ backgroundColor: guide.accent }}
                  >
                    {t('Got it')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      </div>
    );
  }

  // If job failed, redirect back to dashboard without showing error screen
  if (isJobFailed) {
    return null;
  }

  // Show skeleton loading when job is still being transcribed or loading
  if (isJobProcessing || isLoadingServer) {
    return <EditorSkeleton />;
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-var(--welcome-banner-height,0px))] font-sans overflow-hidden relative transition-colors duration-300 bg-surface-100 text-slate-900">
      {/* Background Grid Pattern */}
      <div 
        className="absolute inset-0 z-0 pointer-events-none opacity-40"
        style={{ 
          backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', 
          backgroundSize: '24px 24px' 
        }} 
      />

      {/* File Input Panel (top bar) - only show when not loading from server */}
      {!jobId && <FileInputPanel onLoadData={handleLoadData} disableAutoLoad={false} />}
        
      {dataLoaded && captionData.skeleton.length > 0 ? (
        <>
          {/* Header */}
          <header 
            className="h-16 px-6 lg:px-10 backdrop-blur-md border-b flex items-center justify-between shrink-0 z-30 relative transition-colors bg-white/80 border-slate-100"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            {/* Left: Back + Project Name */}
            <div className="flex items-center gap-6 w-1/3" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', width: '33.333%' }}>
              <button 
                onClick={handleBack}
                className="p-2 rounded-full transition-colors hover:bg-slate-50 text-slate-400 hover:text-slate-900"
              >
                <ChevronLeftIcon />
              </button>
              
              <div className="flex flex-col">
                <input 
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="text-sm font-bold bg-transparent border-none focus:ring-0 focus:outline-none p-0 hover:text-brand transition-colors w-48 lg:w-64 truncate text-slate-900"
                />
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${isSaving ? 'bg-amber-500' : 'bg-emerald-500'}`}></span>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    {t(isSaving ? 'Saving...' : 'Saved')}
                  </span>
                </div>
              </div>
            </div>

            {/* Center: View Mode Toggle */}
            <div className="flex items-center justify-center w-1/3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '33.333%' }}>
              <div className="flex items-center bg-slate-100/80 rounded-full p-1 border border-slate-100" data-tour="view-mode-toggle">
                <button 
                  onClick={() => setViewMode('simple')} 
                  className={`px-3 h-8 flex items-center justify-center rounded-full text-xs font-bold transition-all tooltip-trigger ${
                    viewMode === 'simple' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-tooltip={t('Simple View')}
                  aria-label={t('Simple View')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1.5">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                  {t('Simple')}
                </button>
                <button 
                  onClick={() => setViewMode('studio')} 
                  className={`px-3 h-8 flex items-center justify-center rounded-full text-xs font-bold transition-all tooltip-trigger ${
                    viewMode === 'studio' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-tooltip={t('Studio View')}
                  aria-label={t('Studio View')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1.5">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                  </svg>
                  {t('Studio')}
                </button>
              </div>
            </div>

            {/* Right: Controls */}
            <div className="flex items-center justify-end gap-3 w-1/3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', width: '33.333%' }}>
              
              {/* Undo/Redo */}
              <div className="flex items-center me-1 bg-slate-100/80 rounded-full p-1 border border-slate-100" data-tour="undo-redo">
                <button 
                  onClick={undo} 
                  disabled={!canUndo} 
                  className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 hover:text-slate-900 hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-all tooltip-trigger"
                  data-tooltip={t('Undo (Ctrl+Z)')}
                  aria-label={t('Undo')}
                >
                  <UndoIcon />
                </button>
                <div className="w-px h-4 bg-slate-200 mx-0.5"></div>
                <button 
                  onClick={redo} 
                  disabled={!canRedo} 
                  className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 hover:text-slate-900 hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-all tooltip-trigger"
                  data-tooltip={t('Redo (Ctrl+Shift+Z)')}
                  aria-label={t('Redo')}
                >
                  <RedoIcon />
                </button>
              </div>

              {/* Translation View Toggle (when translation available) */}
              {(translationData || serverTranslation) && (
                <div className="flex items-center bg-slate-100/80 rounded-full p-1 border border-slate-100">
                  <button 
                    onClick={() => setIsTranslationView(false)}
                    className={`px-3 h-8 flex items-center justify-center rounded-full text-xs font-bold transition-all ${
                      !isTranslationView 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t('Original')}
                  </button>
                  <button 
                    onClick={() => setIsTranslationView(true)}
                    className={`px-3 h-8 flex items-center justify-center rounded-full text-xs font-bold transition-all ${
                      isTranslationView 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <GlobeIcon />
                    <span className="ms-1">{t('English')}</span>
                  </button>
                </div>
              )}

              {/* Translate Button - only show if not yet translated */}
              {translationStatus !== 'completed' && (
                <div className="relative" ref={translateMenuRef}>
                  <button 
                    onClick={() => setIsTranslateModalOpen(true)}
                    disabled={isRequestingTranslation}
                    className={`h-10 flex items-center justify-center gap-2 rounded-full transition-all ${
                      translationStatus === 'pending'
                        ? 'px-4 bg-amber-50 text-amber-700 border border-amber-200'
                        : translationStatus === 'none' && !translationRequested
                        ? 'ps-4 pe-3 bg-slate-900 text-white hover:bg-slate-800 active:scale-[0.98]'
                        : 'px-4 bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-[0.98]'
                    }`}
                  >
                    {translationStatus === 'pending' ? (
                      <LoadingIcon />
                    ) : (
                      <TranslateIcon />
                    )}
                    <span className="text-xs font-bold">
                      {t(translationStatus === 'pending' ? 'Translating...' : 'Translate')}
                    </span>
                    {/* NEW indicator - inline pill */}
                    {translationStatus === 'none' && !translationRequested && (
                      <span className="ms-1 px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-bold rounded uppercase">
                        {t('New')}
                      </span>
                    )}
                  </button>

                  {/* Translate Modal */}
                  {isTranslateModalOpen && (
                    <div className="absolute end-0 mt-3 w-[340px] rounded-2xl border border-slate-200 bg-white shadow-2xl p-5 z-50">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-white">
                            <TranslateIcon />
                          </div>
                          <span className="text-sm font-bold text-slate-900">{t('Translate Captions')}</span>
                        </div>
                        <button 
                          onClick={() => setIsTranslateModalOpen(false)}
                          className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          <XIcon />
                        </button>
                      </div>

                      {translationStatus === 'pending' ? (
                        <div className="text-center py-6">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mx-auto mb-4">
                            <div className="animate-spin">
                              <LoadingIcon />
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-slate-900">{t('Translation in Progress')}</p>
                          <p className="text-xs text-slate-500 mt-1">{t('This usually takes 10-30 seconds...')}</p>
                          <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('Select Language')}</div>
                          <div className="space-y-2">
                            {/* English - Available */}
                            <button
                              onClick={() => handleRequestTranslation('en')}
                              disabled={isRequestingTranslation || translationRequested}
                              className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-slate-200 hover:border-violet-400 hover:bg-violet-50 transition-all text-start disabled:opacity-50 disabled:cursor-not-allowed group"
                            >
                              <span className="text-2xl">🇺🇸</span>
                              <div className="flex-1">
                                <div className="text-sm font-bold text-slate-900 group-hover:text-violet-700">{t('English')}</div>
                                <div className="text-xs text-slate-500">{t('American English')}</div>
                              </div>
                              <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full">{t('Available')}</span>
                            </button>
                            
                            {/* Spanish - Coming Soon */}
                            <div className="flex items-center gap-3 p-3.5 rounded-xl border-2 border-slate-100 bg-slate-50/50 opacity-60 cursor-not-allowed">
                              <span className="text-2xl">🇪🇸</span>
                              <div className="flex-1">
                                <div className="text-sm font-bold text-slate-400">{t('Spanish')}</div>
                                <div className="text-xs text-slate-400">Español</div>
                              </div>
                              <span className="px-2.5 py-1 bg-slate-200 text-slate-500 text-[10px] font-bold rounded-full">{t('Coming Soon')}</span>
                            </div>
                            
                            {/* Portuguese - Coming Soon */}
                            <div className="flex items-center gap-3 p-3.5 rounded-xl border-2 border-slate-100 bg-slate-50/50 opacity-60 cursor-not-allowed">
                              <span className="text-2xl">🇧🇷</span>
                              <div className="flex-1">
                                <div className="text-sm font-bold text-slate-400">{t('Portuguese')}</div>
                                <div className="text-xs text-slate-400">Português</div>
                              </div>
                              <span className="px-2.5 py-1 bg-slate-200 text-slate-500 text-[10px] font-bold rounded-full">{t('Coming Soon')}</span>
                            </div>
                            
                            {/* French - Coming Soon */}
                            <div className="flex items-center gap-3 p-3.5 rounded-xl border-2 border-slate-100 bg-slate-50/50 opacity-60 cursor-not-allowed">
                              <span className="text-2xl">🇫🇷</span>
                              <div className="flex-1">
                                <div className="text-sm font-bold text-slate-400">{t('French')}</div>
                                <div className="text-xs text-slate-400">Français</div>
                              </div>
                              <span className="px-2.5 py-1 bg-slate-200 text-slate-500 text-[10px] font-bold rounded-full">{t('Coming Soon')}</span>
                            </div>
                          </div>
                          
                          {translationRequested && !serverTranslation && (
                            <p className="mt-4 text-xs text-amber-600 text-center bg-amber-50 rounded-lg p-2">
                              ⏳ {t('Translation already requested. Please wait...')}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Settings Toggle */}
              <button 
                onClick={() => {
                  const willOpen = !isSettingsOpen;
                  setIsSettingsOpen(willOpen);
                  // Show settings tour on first open
                  if (willOpen && !hasShownSettingsTourRef.current && shouldShowSettingsTour()) {
                    hasShownSettingsTourRef.current = true;
                    // Small delay to let panel render
                    setTimeout(() => setShowSettingsTour(true), 300);
                  }
                }} 
                data-tour="settings-toggle"
                className={`h-10 px-3 flex items-center justify-center gap-2 rounded-full transition-colors ${
                  isSettingsOpen 
                    ? 'bg-slate-100 text-slate-900 shadow-inner' 
                    : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                }`}
              >
                <SettingsIcon />
                <span className="text-xs font-bold">{t('Settings')}</span>
              </button>

              <LanguageSwitcher compact className="rounded-full" />

              {/* Export Button + Modal */}
              <div className="relative" ref={exportMenuRef}>
                <button 
                  onClick={handleExportClick}
                  data-tour="export-button"
                  className="h-10 px-6 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-full shadow-lg shadow-slate-900/10 flex items-center gap-2 transition-all active:scale-[0.97]"
                >
                  <DownloadIcon />
                  {t('Export')}
                </button>

                {isExportOpen && (
                  <div className="absolute end-0 mt-3 w-[360px] rounded-2xl border border-slate-200 bg-white shadow-xl p-4 z-50">
                    {/* Export Source Selection (show only if translation available) */}
                    {(translationData || serverTranslation) && (
                      <>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Export source')}</div>
                        <div className="mt-2 grid grid-cols-2 gap-2 bg-slate-50 p-1 rounded-xl border border-slate-200 mb-4">
                          <button
                            onClick={() => setExportSource('original')}
                            className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                              exportSource === 'original'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                            }`}
                          >
                            🇲🇦 {t('Original (Darija)')}
                          </button>
                          <button
                            onClick={() => setExportSource('translated')}
                            className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                              exportSource === 'translated'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                            }`}
                          >
                            🇺🇸 {t('Translated (English)')}
                          </button>
                        </div>
                      </>
                    )}
                    
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Export format')}</div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <button
                        className={`px-3 py-2 rounded-xl text-sm font-bold transition-all ${
                          exportFormat === 'srt' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        onClick={() => setExportFormat('srt')}
                      >
                        SRT
                      </button>
                      {/* VTT export temporarily disabled
                      <button
                        className={`px-3 py-2 rounded-xl text-sm font-bold transition-all ${
                          exportFormat === 'vtt' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        onClick={() => setExportFormat('vtt')}
                      >
                        VTT
                      </button>
                      */}
                      <button
                        className="px-3 py-2 rounded-xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200"
                        onClick={() => handleExport('txt')}
                      >
                        TXT
                      </button>
                      {/* JSON export temporarily disabled
                      <button
                        className="px-3 py-2 rounded-xl text-sm font-bold bg-slate-100 text-slate-700 hover:bg-slate-200"
                        onClick={() => handleExport('json')}
                      >
                        JSON
                      </button>
                      */}
                    </div>

                    <div className="mt-4">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Export for editor')}</div>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        {Object.values(NLE_EXPORT_GUIDES).map((guide) => {
                          const Logo = guide.Logo;
                          return (
                            <button
                              key={guide.id}
                              className="px-2.5 py-2.5 rounded-xl text-[11px] font-bold bg-slate-50 border border-slate-200 text-slate-800 hover:bg-white hover:border-slate-300 transition-all flex items-center gap-2"
                              onClick={() => handleNleExport(guide.id)}
                            >
                              <Logo size={26} />
                              <span className="leading-tight text-start">{guide.name.replace('Adobe ', '')}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {exportFormat === 'srt' && (
                      <div className="mt-4 border-t border-slate-100 pt-4">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('Chunking for export')}</div>

                      <div className="mt-3 grid grid-cols-2 gap-2 bg-slate-50 p-1 rounded-xl border border-slate-200">
                        <button
                          onClick={() => handleExportSettingChange('chunkMode', 'words')}
                          className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                            (exportChunkSettings.chunkMode || 'words') === 'words'
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {t('By Words')}
                        </button>
                        <button
                          onClick={() => handleExportSettingChange('chunkMode', 'characters')}
                          className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                            exportChunkSettings.chunkMode === 'characters'
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {t('By Characters')}
                        </button>
                      </div>

                      {(exportChunkSettings.chunkMode || 'words') === 'words' ? (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs font-bold text-slate-600">
                            <span>{t('Words per chunk')}</span>
                            <span className="text-brand">{exportChunkSettings.wordsPerChunk}</span>
                          </div>
                          <input
                            type="range"
                            min="1"
                            max="12"
                            value={exportChunkSettings.wordsPerChunk}
                            onChange={(e) => handleExportSettingChange('wordsPerChunk', parseInt(e.target.value))}
                            className="w-full h-2 rounded-full appearance-none cursor-pointer accent-brand bg-slate-200 mt-2"
                          />
                          <div className="mt-2 text-[11px] text-slate-500">
                            {rich(t('Tip: set to <b>1 word</b> for word‑level chunks.'), { b: (c) => <strong>{c}</strong> })}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs font-bold text-slate-600">
                            <span>{t('Characters per chunk')}</span>
                            <span className="text-brand">{exportChunkSettings.charsPerChunk || 50}</span>
                          </div>
                          <input
                            type="range"
                            min="10"
                            max="150"
                            step="5"
                            value={exportChunkSettings.charsPerChunk || 50}
                            onChange={(e) => handleExportSettingChange('charsPerChunk', parseInt(e.target.value))}
                            className="w-full h-2 rounded-full appearance-none cursor-pointer accent-brand bg-slate-200 mt-2"
                          />
                        </div>
                      )}

                      <label className="mt-3 flex items-center justify-between cursor-pointer">
                        <div className={`text-xs transition-colors ${exportChunkSettings.uppercaseEverything ? 'text-brand font-bold' : 'text-slate-600'}`}>{t('Uppercase everything')}</div>
                        <div 
                          className={`relative shrink-0 rounded-full transition-colors ${exportChunkSettings.uppercaseEverything ? 'bg-brand' : 'bg-slate-300'}`}
                          style={{ width: 44, height: 24 }}
                        >
                          <input
                            type="checkbox"
                            checked={exportChunkSettings.uppercaseEverything || false}
                            onChange={(e) => handleExportSettingChange('uppercaseEverything', e.target.checked)}
                            className="sr-only"
                          />
                          <span 
                            className="absolute bg-white rounded-full shadow transition-all duration-200"
                            style={{ 
                              width: 18, 
                              height: 18, 
                              top: 3,
                              insetInlineStart: exportChunkSettings.uppercaseEverything ? 23 : 3
                            }}
                          />
                        </div>
                      </label>

                      <label className="mt-3 flex items-center justify-between cursor-pointer">
                        <div className={`text-xs transition-colors ${exportChunkSettings.silenceAwareChunks ? 'text-brand font-bold' : 'text-slate-600'}`}>{t('Silence-aware chunks')}</div>
                        <div 
                          className={`relative shrink-0 rounded-full transition-colors ${exportChunkSettings.silenceAwareChunks ? 'bg-brand' : 'bg-slate-300'}`}
                          style={{ width: 44, height: 24 }}
                        >
                          <input
                            type="checkbox"
                            checked={exportChunkSettings.silenceAwareChunks || false}
                            onChange={(e) => handleExportSettingChange('silenceAwareChunks', e.target.checked)}
                            className="sr-only"
                          />
                          <span 
                            className="absolute bg-white rounded-full shadow transition-all duration-200"
                            style={{ 
                              width: 18, 
                              height: 18, 
                              top: 3,
                              insetInlineStart: exportChunkSettings.silenceAwareChunks ? 23 : 3
                            }}
                          />
                        </div>
                      </label>

                      <label className="mt-3 flex items-center justify-between cursor-pointer">
                        <div className={`text-xs transition-colors ${exportChunkSettings.punctuationAwareChunks ? 'text-brand font-bold' : 'text-slate-600'}`}>{t('Punctuation-aware chunks')}</div>
                        <div 
                          className={`relative shrink-0 rounded-full transition-colors ${exportChunkSettings.punctuationAwareChunks ? 'bg-brand' : 'bg-slate-300'}`}
                          style={{ width: 44, height: 24 }}
                        >
                          <input
                            type="checkbox"
                            checked={exportChunkSettings.punctuationAwareChunks || false}
                            onChange={(e) => handleExportSettingChange('punctuationAwareChunks', e.target.checked)}
                            className="sr-only"
                          />
                          <span 
                            className="absolute bg-white rounded-full shadow transition-all duration-200"
                            style={{ 
                              width: 18, 
                              height: 18, 
                              top: 3,
                              insetInlineStart: exportChunkSettings.punctuationAwareChunks ? 23 : 3
                            }}
                          />
                        </div>
                      </label>

                      <button
                        onClick={handleApplyExportChunking}
                        className="mt-3 w-full px-3 py-2 rounded-xl text-xs font-bold bg-slate-900 text-white hover:bg-slate-800"
                      >
                        {t('Reorganize')}
                      </button>

                      <button
                        onClick={() => handleExport(exportFormat)}
                        className="mt-2 w-full px-3 py-2 rounded-xl text-xs font-bold bg-brand text-white hover:bg-brand/90"
                      >
                        {t('Download {format}', { format: exportFormat.toUpperCase() })}
                      </button>
                    </div>
                    )}

                    <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                      {t(exportFormat === 'srt'
                        ? 'SRT export using Studio chunking. Or pick an editor below to download SRT and get import steps.'
                        : 'Select SRT to customize chunking, or export for Premiere, DaVinci, CapCut, or Final Cut.')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Search Bar */}
          <SearchBar
            isOpen={search.isSearchOpen}
            searchQuery={search.searchQuery}
            replaceQuery={search.replaceQuery}
            showReplace={search.showReplace}
            matches={search.matches}
            currentMatchIndex={search.currentMatchIndex}
            matchCase={search.matchCase}
            useRegex={search.useRegex}
            wholeWord={search.wholeWord}
            onClose={search.closeSearch}
            onSearchChange={search.setSearchQuery}
            onReplaceChange={search.setReplaceQuery}
            onNextMatch={search.goToNextMatch}
            onPrevMatch={search.goToPreviousMatch}
            onReplaceCurrent={search.replaceCurrentMatch}
            onReplaceAll={search.replaceAllMatches}
            onToggleMatchCase={search.toggleMatchCase}
            onToggleRegex={search.toggleUseRegex}
            onToggleWholeWord={search.toggleWholeWord}
            onToggleReplace={search.toggleShowReplace}
            onKeyDown={search.handleKeyDown}
          />

          {/* Trial Protection Banner (hidden while editor tour is active to avoid overlap) */}
          {isTrialProtected && !showEditorTour && (
            <div className="border-b border-brand/10" style={{ backgroundColor: '#fff0f1' }}>
              <div className="py-3.5 flex items-center justify-center gap-4 max-w-3xl mx-auto">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <p className="text-[15px] font-semibold text-slate-700">
                  {t('Viewing 50% of transcript')}
                  <span className="text-slate-400 mx-2">·</span>
                  <span className="text-slate-500 font-normal">{t('Verify your phone to unlock full content')}</span>
                </p>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <button
                    onClick={() => setShowVerificationModal(true)}
                    className="px-5 py-2 bg-brand hover:bg-brand-dark text-white text-sm font-bold rounded-lg transition-all active:scale-[0.98]"
                  >
                    {t('Verify')}
                  </button>
                  <button
                    onClick={handleUpgradeClick}
                    className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-lg transition-all active:scale-[0.98]"
                  >
                    {t('Upgrade')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 flex overflow-hidden relative z-10">
            {/* Editor Content */}
            <div className="flex-1 flex flex-col relative min-w-0 transition-all duration-300 ease-in-out">
              {/* Scrollable Editor Area */}
              <div className="flex-1 overflow-y-auto scroll-smooth custom-scrollbar" id="editor-scroll">
                <div className={`mx-auto px-4 md:px-12 pt-10 pb-48 min-h-full ${viewMode === 'studio' ? 'max-w-4xl' : 'max-w-5xl'}`}>
                  {viewMode === 'studio' ? (
                    <CaptionEditor
                      searchMatchIds={search.matchingWordIds}
                      currentSearchMatchId={search.currentMatch?.wordId}
                      autoSelectWordId={autoSelectWordId}
                      onAutoSelectHandled={() => setAutoSelectWordId(null)}
                      projectLanguage={projectLanguage}
                      isTrialProtected={isTrialProtected}
                      onVerifyClick={() => setShowVerificationModal(true)}
                      onUpgradeClick={handleUpgradeClick}
                    />
                  ) : (
                    <TextProseRenderer 
                      searchMatchIds={search.matchingWordIds}
                      currentSearchMatchId={search.currentMatch?.wordId}
                      projectLanguage={projectLanguage}
                      isTrialProtected={isTrialProtected}
                      onVerifyClick={() => setShowVerificationModal(true)}
                      onSwitchToStudio={(wordId) => {
                        setAutoSelectWordId(wordId);
                        setViewMode('studio');
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Floating Audio Player */}
              {videoSrc && <VideoPreview />}
              
              {/* Tips Panel - disabled for now */}
              {/* <TipsPanel /> */}
              
              {/* Tutorials Panel */}
              <TutorialsPanel onStartTour={() => setShowEditorTour(true)} />
              <PremiereEditorCard />
            </div>

            {/* Right Settings Panel */}
            <div className={`hidden lg:flex flex-col backdrop-blur-md border-s transition-all duration-300 ease-in-out bg-white/80 border-slate-100 ${
              isSettingsOpen ? 'w-80 opacity-100' : 'w-0 opacity-0 overflow-hidden border-none'
            }`}>
              {isSettingsOpen && (
                <>
                  <div className="p-6 border-b flex items-center justify-between border-slate-100">
                    <h3 className="font-bold flex items-center gap-2 text-slate-900">
                      <SettingsIcon />
                      {t('Settings')}
                    </h3>
                    <button 
                      onClick={() => setIsSettingsOpen(false)} 
                      className="transition-colors text-slate-400 hover:text-slate-900"
                    >
                      <XIcon />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
              <Sidebar projectLanguage={projectLanguage} />
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Skeleton Loading */
        <EditorContentSkeleton />
      )}

        {confirmState.isOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => closeConfirm(false)} />
            <div className="relative bg-white rounded-3xl w-full max-w-md p-6 md:p-8 shadow-2xl border border-slate-100 animate-[scaleUp_0.2s_ease-out]">
              <h3 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2">{t(confirmState.title)}</h3>
              <p className="text-slate-500 text-sm mb-6">{t(confirmState.message)}</p>
              <div className="flex flex-col-reverse md:flex-row md:justify-end gap-3">
                <button
                  type="button"
                  onClick={() => closeConfirm(false)}
                  className="px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  {t(confirmState.cancelLabel)}
                </button>
                <button
                  type="button"
                  onClick={() => closeConfirm(true)}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-[#f73140] text-white hover:bg-[#d62331] shadow-lg shadow-[#f73140]/20 transition-all"
                >
                  {t(confirmState.confirmLabel)}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Editor Tour */}
      <EditorTour
        isOpen={showEditorTour}
        onComplete={() => setShowEditorTour(false)}
        onSkip={() => setShowEditorTour(false)}
      />

      {/* Settings Tour */}
      <SettingsTour
        isOpen={showSettingsTour}
        onComplete={() => setShowSettingsTour(false)}
        onSkip={() => setShowSettingsTour(false)}
      />

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-[#f5f5f5] rounded-2xl sm:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto relative">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="absolute top-3 end-3 z-10 w-7 h-7 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 flex items-center justify-center transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="text-center pt-6 sm:pt-8 pb-3 sm:pb-4 px-4 sm:px-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900">{t('Our Pricing Options')}</h2>
            </div>

            <div className="px-3 sm:px-6 pb-6 sm:pb-8">
              <div className="mb-4 sm:mb-6 relative">
                <div className="bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-5 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#f73140]/20 via-purple-500/20 to-[#f73140]/20 animate-pulse" />
                  <div className="relative flex flex-col items-center text-center gap-3">
                    <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/10 rounded-full">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                        </span>
                        <span className="text-[10px] sm:text-xs text-slate-300 font-medium">{t('12 people viewing')}</span>
                      </div>

                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-[#f73140] to-orange-500 rounded-full">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-white">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span className="text-[10px] sm:text-xs font-bold text-white uppercase tracking-wider">{t('Ends Soon')}</span>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg sm:text-xl font-black text-white mb-1">{t('Special Launch Prices')}</h3>
                      <p className="text-xs sm:text-sm text-slate-400">{t('Lock in these exclusive rates before they increase')}</p>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 pt-2 border-t border-white/10 w-full">
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-2 rtl:space-x-reverse">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-slate-900 flex items-center justify-center text-[8px] font-bold text-white">A</div>
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-slate-900 flex items-center justify-center text-[8px] font-bold text-white">M</div>
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 border-2 border-slate-900 flex items-center justify-center text-[8px] font-bold text-white">S</div>
                        </div>
                        <span className="text-[10px] sm:text-xs text-slate-400">
                          {rich(t('<b>47 creators</b> joined this week'), { b: (c) => <span className="text-white font-semibold">{c}</span> })}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2" className="flex-shrink-0">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <span className="text-[10px] sm:text-xs text-slate-400">
                          {rich(t('Only <b>8 spots</b> left at this price'), { b: (c) => <span className="text-[#f73140] font-bold">{c}</span> })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col">
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-1 sm:mb-2">STARTER</h3>
                    <p className="text-xs sm:text-sm text-slate-500">{t('Perfect for beginner video editors who are just getting started.')}</p>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                    <li className="flex items-center gap-2 text-slate-700"><span className="text-[#f73140]">✓</span><span className="text-xs sm:text-sm">{t('8 videos')}</span></li>
                    <li className="flex items-center gap-2 text-slate-700"><span className="text-[#f73140]">✓</span><span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span></li>
                    <li className="flex items-center gap-2 text-slate-700"><span className="text-[#f73140]">✓</span><span className="text-xs sm:text-sm">{t(planPrices.starter.perVideo)}</span></li>
                    <li className="flex items-center gap-2 text-slate-700"><span className="text-[#f73140]">✓</span><span className="text-xs sm:text-sm">{t('Email support')}</span></li>
                  </ul>

                  <div className="mt-auto">
                    <div className="text-center mb-3 sm:mb-4">
                      <span className="text-3xl sm:text-4xl font-black text-slate-900">{t(planPrices.starter.display)}</span>
                      <span className="text-slate-500 text-xs sm:text-sm">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('starter')}
                      disabled={upgradeLoading || userPlan === 'starter'}
                      className={`w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all ${
                        userPlan === 'starter'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'starter' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GET STARTED')}
                    </button>
                  </div>
                </div>

                <div className="bg-[#f73140] rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg flex flex-col relative order-first md:order-none">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-slate-900 text-white text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-wide">{t('Popular')}</span>
                  </div>

                  <div className="mb-3 sm:mb-4 mt-2 sm:mt-0">
                    <h3 className="text-lg sm:text-xl font-black text-white mb-1 sm:mb-2">PRO</h3>
                    <p className="text-xs sm:text-sm text-white/80">{t('Great for active editors working with more clients and content.')}</p>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                    <li className="flex items-center gap-2 text-white"><span>✓</span><span className="text-xs sm:text-sm">{t('30 videos')}</span></li>
                    <li className="flex items-center gap-2 text-white"><span>✓</span><span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span></li>
                    <li className="flex items-center gap-2 text-white"><span>✓</span><span className="text-xs sm:text-sm">{t(planPrices.pro.perVideo)}</span></li>
                    <li className="flex items-center gap-2 text-white"><span>✓</span><span className="text-xs sm:text-sm">{t('Priority support')}</span></li>
                  </ul>

                  <div className="mt-auto">
                    <div className="text-center mb-3 sm:mb-4">
                      <span className="text-3xl sm:text-4xl font-black text-white">{<WelcomePrice plan="pro" price={planPrices.pro} />}</span>
                      <span className="text-white/80 text-xs sm:text-sm">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('pro')}
                      disabled={upgradeLoading || userPlan === 'pro'}
                      className={`w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all ${
                        userPlan === 'pro'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-white text-[#f73140] hover:bg-slate-50 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'pro' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GO PRO')}
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col">
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-1 sm:mb-2">MAX</h3>
                    <p className="text-xs sm:text-sm text-slate-500">{t('Built for professional editors handling large content demands.')}</p>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                    <li className="flex items-center gap-2 text-slate-700"><span className="text-[#f73140]">✓</span><span className="text-xs sm:text-sm">{t('100 videos')}</span></li>
                    <li className="flex items-center gap-2 text-slate-700"><span className="text-[#f73140]">✓</span><span className="text-xs sm:text-sm">{t('Up to 2 minutes per video')}</span></li>
                    <li className="flex items-center gap-2 text-slate-700"><span className="text-[#f73140]">✓</span><span className="text-xs sm:text-sm">{t(planPrices.max.perVideo)}</span></li>
                    <li className="flex items-center gap-2 text-slate-700"><span className="text-[#f73140]">✓</span><span className="text-xs sm:text-sm">{t('Dedicated support')}</span></li>
                  </ul>

                  <div className="mt-auto">
                    <div className="text-center mb-3 sm:mb-4">
                      <span className="text-3xl sm:text-4xl font-black text-slate-900">{<WelcomePrice plan="max" price={planPrices.max} />}</span>
                      <span className="text-slate-500 text-xs sm:text-sm">{t('/month')}</span>
                    </div>
                    <button
                      onClick={() => handleUpgradeToPlan('max')}
                      disabled={upgradeLoading || userPlan === 'max'}
                      className={`w-full py-3 sm:py-3.5 rounded-full font-bold text-xs sm:text-sm transition-all ${
                        userPlan === 'max'
                          ? 'bg-green-500 text-white cursor-default'
                          : 'bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50'
                      }`}
                    >
                      {t(userPlan === 'max' ? 'CURRENT PLAN' : upgradeLoading ? 'Processing...' : 'GET PREMIUM')}
                    </button>
                  </div>
                </div>
              </div>

              {billingError && (
                <div className="mt-3 sm:mt-4 p-3 sm:p-4 rounded-xl bg-red-50 border border-red-100">
                  <p className="text-xs sm:text-sm text-red-600 text-center">{t(billingError)}</p>
                </div>
              )}

              <div className="flex flex-wrap justify-center gap-3 sm:gap-6 mt-5 sm:mt-8">
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600"><span className="text-[#f73140]">✓</span><span>{t('Cancel anytime')}</span></div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600"><span className="text-[#f73140]">✓</span><span>{t('Secure payment')}</span></div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600"><span className="text-[#f73140]">✓</span><span>{t('Instant activation')}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onVerified={handleVerificationComplete}
        onUpgrade={handleUpgradeClick}
        phoneNumber={user?.phone_number}
      />

      {nleGuideId && NLE_EXPORT_GUIDES[nleGuideId] && (() => {
        const guide = NLE_EXPORT_GUIDES[nleGuideId];
        const Logo = guide.Logo;
        return (
          <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative m-4">
              <button
                onClick={() => setNleGuideId(null)}
                className="absolute top-3 end-3 z-10 w-8 h-8 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-colors"
                aria-label={t('Close')}
              >
                <XIcon />
              </button>
              <div className="p-6 pt-8">
                <div className="flex items-center gap-3 mb-2">
                  <Logo size={40} />
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t('SRT downloaded')}</p>
                    <h3 className="text-xl font-black text-slate-900">{t('Import into {app}', { app: guide.name })}</h3>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mb-5">
                  {t('Follow these steps to add your subtitles in {app}.', { app: guide.name })}
                </p>
                <ol className="space-y-3">
                  {guide.steps.map((step, index) => (
                    <li key={index} className="flex gap-3">
                      <span
                        className="shrink-0 w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center mt-0.5"
                        style={{ backgroundColor: guide.accent }}
                      >
                        {index + 1}
                      </span>
                      <p className="text-sm text-slate-700 leading-relaxed">{t(step)}</p>
                    </li>
                  ))}
                </ol>
                <button
                  onClick={() => setNleGuideId(null)}
                  className="mt-6 w-full px-4 py-3 rounded-xl text-sm font-bold text-white transition-colors"
                  style={{ backgroundColor: guide.accent }}
                >
                  {t('Got it')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function EditorApp() {
  return (
    <CaptionProvider>
      <EditorPage />
    </CaptionProvider>
  );
}

export default EditorApp;
